import { QueryClient } from "@tanstack/react-query";
import { camelizeKeys, decamelizeKeys } from "./case-converter";

// Bare defaults on purpose: the site issues no queries, and both mutations
// declare their own retry policy — options configured here would govern nothing.
export const apiQueryClient = new QueryClient();

export interface ApiResponse<T> {
  status: number;
  data: T;
  headers: Headers;
  /**
   * True when `fetch` followed a redirect to produce this response — the one
   * legitimate way a 2xx arrives without a JSON body (Devise answers an
   * already-signed-in re-submit with a 302 the client follows).
   */
  redirected: boolean;
}

export class HttpError extends Error {
  status: number;
  data: unknown;
  constructor(status: number, data: unknown) {
    super(`HTTP Error ${status}`);
    this.status = status;
    this.data = data;
  }
}

/**
 * A 2xx whose body would not parse as JSON and which is not a followed
 * redirect or a bodyless status. Resolving it as `{}` used to be
 * indistinguishable from the redirect-follow case, which let a proxy error
 * page read as a successful sign-in. Not an HttpError on purpose: the forms
 * branch HttpError-by-status, and this failure has no trustworthy status to
 * branch on — it lands on their connection-failed copy instead.
 */
export class MalformedResponseError extends Error {
  status: number;
  constructor(status: number) {
    super(`Malformed ${status} response`);
    this.status = status;
  }
}

/**
 * The CSRF token Rails prints into the layout head via `csrf_meta_tags`.
 *
 * Read on every request and never cached: Rails rotates the token when an
 * operator signs in, so a value captured once at module load would be stale for
 * the rest of the document's life. Returns null when the document carries no
 * tag — a page rendered without `csrf_meta_tags`, or a non-document context —
 * because omitting the header entirely beats sending an empty one.
 */
function readCsrfToken(): string | null {
  const tag = document.querySelector('meta[name="csrf-token"]');
  return tag?.getAttribute("content") || null;
}

/**
 * Bounds every request: past this, fetch rejects with a TimeoutError
 * DOMException, which the forms read as their connection-failed state. Without
 * it a hung connection held `isPending` forever — both mutations retry
 * nothing, so nothing else ever broke the wait.
 */
export const REQUEST_TIMEOUT_MS = 10_000;

async function request<T>(
  method: string,
  url: string,
  body?: unknown,
): Promise<ApiResponse<T>> {
  const headers: Record<string, string> = {};
  // The operator session is a Rails cookie rather than a bearer token, and
  // `fetch` omits cookies unless asked — without this the management endpoints
  // would answer 401 to a signed-in operator.
  const options: RequestInit = {
    method,
    headers,
    credentials: "same-origin",
    signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
  };

  // A cookie the browser attaches by itself is forgeable from another origin,
  // so Rails guards the unsafe verbs with `protect_from_forgery`. Every request
  // this client sends is a POST, so the token always rides along when the
  // document carries one.
  const csrfToken = readCsrfToken();
  if (csrfToken) headers["X-CSRF-Token"] = csrfToken;

  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    options.body = JSON.stringify(decamelizeKeys(body));
  }

  const response = await fetch(url, options);

  let data: unknown = {};
  try {
    data = await response.json();
  } catch {
    // No JSON body. Legitimate for a bodyless status, a followed redirect's
    // landing page, or an error page (the status alone carries the failure);
    // on any other 2xx an empty body is indistinguishable from data loss, so
    // it rejects rather than resolving as `{}`.
    const bodyless = response.status === 204 || response.status === 205;
    if (!bodyless && !response.redirected && response.status < 400) {
      throw new MalformedResponseError(response.status);
    }
  }
  data = camelizeKeys(data);

  // Every failure is a rejection. The site's only caller is the signup form,
  // which reads a thrown HttpError as its retryable error state — resolving a
  // 4xx would show a confirmation to a visitor who is not subscribed.
  if (response.status >= 400) {
    throw new HttpError(response.status, data);
  }

  return {
    status: response.status,
    data: data as T,
    headers: response.headers,
    redirected: response.redirected,
  };
}

// `request` stays verb-generic, and each helper arrives with the caller that
// needs it — the admin subscriber list brought `get` and `delete`. There is
// still no `put` or `patch`: nothing on the site edits a record in place.
export const apiClient = {
  get: <T>(url: string) => request<T>("GET", url),
  post: <T>(url: string, data?: unknown) => request<T>("POST", url, data),
  delete: <T>(url: string) => request<T>("DELETE", url),
};
